import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { DownloadSimple, FileXls } from "@phosphor-icons/react";

function defaultFrom() {
  const d = new Date(); d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/reports/summary", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportCsv = (rows, filename) => {
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("lom_token");
      const url = `${API_BASE}/reports/export.xlsx?date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `lom-timetrack-${dateFrom}_${dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel report downloaded");
    } catch (e) {
      toast.error("Excel export failed: " + e.message);
    }
  };

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Analytics</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Reports</h1>
          <p className="text-zinc-600 mt-1 text-sm">Aggregate attendance over a date range.</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-eyebrow block mb-1.5">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="lom-input" data-testid="report-from-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="lom-input" data-testid="report-to-input" />
        </div>
        <button onClick={load} className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="report-run-btn">Run report</button>
        <button onClick={exportExcel} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-sm flex items-center gap-2" data-testid="report-excel-btn">
          <FileXls size={14} weight="bold" />
          Excel (.xlsx)
        </button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Records" value={data.total_records} />
            <Stat label="Employees covered" value={data.per_user.length} />
            <Stat label="Stores covered" value={data.per_store.length} />
            <Stat label="Range" value={`${data.date_from} → ${data.date_to}`} mono small />
          </div>

          <Section
            title="By employee"
            onExport={() => exportCsv(data.per_user, `employees-${data.date_from}_${data.date_to}.csv`)}
            tid="export-users-btn"
          >
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <Th>Employee</Th><Th className="text-right">Days</Th><Th className="text-right">On Time</Th>
                  <Th className="text-right">Late</Th><Th className="text-right">Overtime</Th>
                  <Th className="text-right">Left Early</Th><Th className="text-right">Worked</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.per_user.map((u) => (
                  <tr key={u.user_id}>
                    <Td className="font-semibold">{u.name}</Td>
                    <Td className="text-right font-mono">{u.days}</Td>
                    <Td className="text-right font-mono">{u.on_time}</Td>
                    <Td className="text-right font-mono text-amber-700">{u.late}</Td>
                    <Td className="text-right font-mono text-blue-700">{u.overtime}</Td>
                    <Td className="text-right font-mono text-orange-700">{u.left_early}</Td>
                    <Td className="text-right font-mono">{Math.floor(u.worked_minutes/60)}h {u.worked_minutes%60}m</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            title="By store"
            onExport={() => exportCsv(data.per_store, `stores-${data.date_from}_${data.date_to}.csv`)}
            tid="export-stores-btn"
          >
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr><Th>Store</Th><Th className="text-right">Records</Th><Th className="text-right">On Time</Th><Th className="text-right">Late</Th></tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.per_store.map((s) => (
                  <tr key={s.store_id}>
                    <Td className="font-semibold">{s.store_name || s.store_id}</Td>
                    <Td className="text-right font-mono">{s.records}</Td>
                    <Td className="text-right font-mono">{s.on_time}</Td>
                    <Td className="text-right font-mono text-amber-700">{s.late}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, mono, small }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 ${small ? "text-sm" : "stat-number"} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, onExport, children, tid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-6">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
        <div>
          <div className="label-eyebrow">Report</div>
          <h3 className="font-heading font-bold text-xl text-zinc-950">{title}</h3>
        </div>
        <button onClick={onExport} className="flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-zinc-50" data-testid={tid}>
          <DownloadSimple size={14} weight="bold" />
          Export CSV
        </button>
      </div>
      {children}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
